/**
 * God-mode panel — a god-only screen reached from the ESC menu.
 *   ITEMS     — grant the ITEMS catalog into your own inventory (server-authoritative).
 *   ENEMIES   — pick an enemy archetype (3 classes × 3 weights) + a count, then
 *               PLACE arms the ground reticle (main wires arm → sendDroneSpawn).
 *   VEHICLES  — spawn an authored chassis or a bounded runtime-tuned clone.
 *   HARNESSES — open a dev harness page (vite dev only; see HARNESSES below).
 * The server is the sole authority for the first two (a request from a non-god connection
 * is a no-op); this panel only renders when net.onGod flagged the connection.
 */
import { HOMESTEAD_PREFABS, ITEMS, VEHICLES, tuneVehicle, type ItemKind, type VehicleDef, type VehicleTuning } from "@hyperlight/shared";

const KIND_ORDER: readonly ItemKind[] = [
  "weapon", "consumable", "ammo", "material", "module",
  // v49 collection tiers: wearable gear, garage keycards, hangar registrations.
  "gear", "vehicle", "ship",
];
const QUANTITIES = [1, 10, 100, 999] as const;
/** CREDITS: quick-grant denominations (label rendered short, e.g. 10K / 1M). */
const CREDIT_AMOUNTS = [100, 1_000, 10_000, 100_000, 1_000_000] as const;

/** Compact a credit amount for a button label: 1000 → "1K", 1_000_000 → "1M". */
function creditLabel(n: number): string {
  if (n >= 1_000_000) return `${n / 1_000_000}M`;
  if (n >= 1_000) return `${n / 1_000}K`;
  return String(n);
}

/**
 * Dev harness pages. Each drives the REAL subsystem (not a mock) in isolation, so the bugs that
 * live in how the runtime layers COMBINE are visible.
 *
 * These are NOT build inputs — vite.config.ts declares no rollupOptions.input, so only index.html
 * ships and these exist solely under `vite dev`. The tab is therefore hidden unless import.meta.env
 * .DEV, or production would show buttons that 404.
 */
const HARNESSES: readonly { path: string; name: string; desc: string }[] = [
  { path: "/animation-lab.html", name: "ANIMATION LAB", desc: "author humanoid clips, events + interaction context" },
  { path: "/asset-lab.html", name: "ASSET LAB", desc: "procedural ships, items + vehicles — model, sockets, gameplay data" },
  { path: "/stance-editor.html", name: "VEHICLE RIGGING", desc: "tune mounted rider FK, hand/foot IK + copy riderPose" },
  { path: "/avatar-harness.html", name: "AVATAR", desc: "real Avatar — clips, aim, lean, foot IK" },
  { path: "/creator-harness.html", name: "CREATOR", desc: "real CharacterCreator — framing, mannequin" },
  { path: "/drone-harness.html", name: "ENEMIES", desc: "real DroneSwarm humanoid path" },
  { path: "/combat-lab.html", name: "COMBAT LAB", desc: "1-on-1 spar — real blade stack vs a launchable dummy" },
  { path: "/vehicle-harness.html", name: "VEHICLES", desc: "drive any chassis — probes, boost, handling, camera + model portal" },
  { path: "/model-compare.html", name: "MODEL COMPARE", desc: "converted chassis side by side — panels, colours, size" },
  { path: "/flora-lab.html", name: "FLORA LAB", desc: "tune SpeciesParams against the reference pack" },
  { path: "/audio-lab.html", name: "AUDIO LAB", desc: "fit synth specs to recordings · A/B · copy manifest entries" },
  { path: "/avatar-lab.html", name: "AVATAR LAB", desc: "body proportions · equipment · animation preview · face capture" },
];

/** ENEMIES grid: one row per class, three weight columns. */
const ENEMY_ROWS: { label: string; kinds: { kind: string; name: string }[] }[] = [
  { label: "FLYING", kinds: [
    { kind: "scout", name: "SCOUT" }, { kind: "gunner", name: "GUNNER" }, { kind: "heavy", name: "HEAVY" } ] },
  // The remaining flyers — the full roster is god-spawnable (2026-08-17).
  { label: "FLYING+", kinds: [
    { kind: "swarmer", name: "SWARMER" }, { kind: "sniper", name: "SNIPER" } ] },
  { label: "GROUNDED", kinds: [
    { kind: "skirmisher", name: "SKIRMISHER" }, { kind: "trooper", name: "TROOPER" }, { kind: "bastion", name: "BASTION" } ] },
  { label: "MELEE", kinds: [
    { kind: "biter", name: "BITER" }, { kind: "mauler", name: "MAULER" }, { kind: "brute", name: "BRUTE" } ] },
  { label: "PLAYER", kinds: [
    { kind: "player_ground", name: "GROUND" }, { kind: "player_flyer", name: "FLYER" }, { kind: "player_brawler", name: "BRAWLER" } ] },
  // The weaponised-human cache rank-and-file (duelist = blade, raider = arm cannon) and
  // the two P5b cache minibosses — spawnable here for live sanity outside a run.
  { label: "CACHE", kinds: [
    { kind: "duelist", name: "DUELIST" }, { kind: "raider", name: "RAIDER" },
    { kind: "warden", name: "WARDEN" }, { kind: "revenant", name: "REVENANT" } ] },
];
const ENEMY_COUNTS = [1, 3, 5, 10] as const;
const VEHICLE_TUNERS: readonly {
  key: keyof VehicleTuning;
  label: string;
  value(def: VehicleDef): number;
  step: string;
}[] = [
  { key: "mass", label: "MASS", value: (d) => d.mass, step: "10" },
  { key: "maxSpeed", label: "TOP SPEED", value: (d) => d.handling.maxSpeed, step: "1" },
  { key: "acceleration", label: "ACCEL", value: (d) => d.handling.acceleration, step: "1" },
  { key: "brakeAcceleration", label: "BRAKE", value: (d) => d.handling.brakeAcceleration, step: "1" },
  { key: "lateralGrip", label: "GRIP", value: (d) => d.handling.lateralGrip, step: "0.1" },
  { key: "driftGripMultiplier", label: "DRIFT GRIP ×", value: (d) => d.handling.driftGripMultiplier, step: "0.01" },
  { key: "driftYawMultiplier", label: "DRIFT YAW ×", value: (d) => d.handling.driftYawMultiplier, step: "0.05" },
  { key: "driftDrag", label: "DRIFT DRAG", value: (d) => d.handling.driftDrag, step: "0.05" },
  { key: "driftRecovery", label: "DRIFT RECOVERY", value: (d) => d.handling.driftRecovery, step: "0.5" },
  { key: "yawRateLow", label: "LOW-SPEED YAW", value: (d) => d.handling.yawRateLow, step: "0.05" },
  { key: "yawRateHigh", label: "HIGH-SPEED YAW", value: (d) => d.handling.yawRateHigh, step: "0.05" },
  { key: "rideHeight", label: "RIDE HEIGHT", value: (d) => d.probes[0]?.rideHeight ?? 1, step: "0.05" },
  { key: "parkedRideHeight", label: "PARKED HEIGHT", value: (d) => d.hover?.parkedRideHeight ?? d.probes[0]?.rideHeight ?? 1, step: "0.05" },
  { key: "hoverLiftResponse", label: "HOVER LIFT RATE", value: (d) => d.hover?.liftResponse ?? 1, step: "0.1" },
  { key: "hoverSettleResponse", label: "HOVER DROP RATE", value: (d) => d.hover?.settleResponse ?? 1, step: "0.1" },
  { key: "springScale", label: "SPRING ×", value: () => 1, step: "0.1" },
  { key: "dampingScale", label: "DAMPING ×", value: () => 1, step: "0.1" },
  { key: "boostCapacity", label: "BOOST ENERGY", value: (d) => d.boost?.capacity ?? 0, step: "1" },
  { key: "boostSpeedMultiplier", label: "BOOST SPEED ×", value: (d) => d.boost?.speedMultiplier ?? 1, step: "0.05" },
  { key: "glideWingArea", label: "GLIDE WING AREA", value: (d) => d.glide.wingArea, step: "0.5" },
  { key: "glideAirDensity", label: "GLIDE AIR DENSITY", value: (d) => d.glide.airDensity, step: "0.05" },
  { key: "glideLiftSlope", label: "GLIDE LIFT SLOPE", value: (d) => d.glide.liftSlope, step: "0.1" },
  { key: "glideZeroLiftAoADeg", label: "GLIDE ZERO-LIFT AOA", value: (d) => d.glide.zeroLiftAoADeg, step: "0.5" },
  { key: "glideStallAoADeg", label: "GLIDE STALL AOA", value: (d) => d.glide.stallAoADeg, step: "0.5" },
  { key: "glideBaseDragCoefficient", label: "GLIDE BASE DRAG", value: (d) => d.glide.baseDragCoefficient, step: "0.005" },
  { key: "glideInducedDragFactor", label: "GLIDE INDUCED DRAG", value: (d) => d.glide.inducedDragFactor, step: "0.005" },
  { key: "glideSideDragCoefficient", label: "GLIDE SIDE DRAG", value: (d) => d.glide.sideDragCoefficient, step: "0.05" },
  { key: "glideMinControlSpeed", label: "GLIDE MIN CONTROL", value: (d) => d.glide.minControlSpeed, step: "1" },
  { key: "glideMaxBankDeg", label: "GLIDE MAX BANK", value: (d) => d.glide.maxBankDeg, step: "1" },
  { key: "glideDetachImpulse", label: "GLIDE DETACH IMPULSE", value: (d) => d.glide.detachImpulse, step: "0.1" },
];

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : "&quot;",
  );
}

export class GodPanel {
  visible = false;
  private el: HTMLElement;
  private qty = 1;
  private enemyKind: string | null = null;
  private enemyCount = 1;
  /** CALM spawns carry a tight guard leash — idle life until provoked. */
  private enemyCalm = false;
  private vehicleId = Object.keys(VEHICLES)[0] ?? "";
  private placeBtn!: HTMLButtonElement;
  private paintPerf: () => void = () => {};

  constructor(
    private handlers: {
      onSpawn: (id: string, n: number) => void;
      /** Add (or, with a negative amount, remove) credits from the player's balance. */
      onAddCredits: (amount: number) => void;
      onSpawnEnemy: (kind: string, count: number, calm: boolean) => void;
      onSpawnVehicle: (def: VehicleDef) => void;
      /** Asset-lab vehicles saved to the player's account (users/{uid}/assets). */
      listAssetVehicles: () => Promise<{ id: string; name: string }[]>;
      onSpawnAssetVehicle: (docId: string) => void;
      onClose: () => void;
      /** Plop a join-ready prefab building ahead of the player (own claim only).
       *  Closes the panel so the placement is visible. */
      onPlopPrefab?: (prefabId: string) => void;
      /** Toggle the live perf overlay (god-gated in engine/perf.ts); returns the new state. */
      onTogglePerf: () => boolean;
      perfOn: () => boolean;
      /** DEV-only: open the in-world cutscene authoring workbench (an in-game overlay, not a
       *  separate harness page — so it lives here as a harness ENTRY, not in the HARNESSES list). */
      onCutscene?: () => void;
    },
  ) {
    const itemGroups = KIND_ORDER.map((kind) => {
      const items = Object.values(ITEMS).filter((d) => d.kind === kind);
      if (!items.length) return "";
      const rows = items
        .map(
          (d) =>
            `<button class="god-item inv-${d.kind}" data-id="${esc(d.id)}" title="${esc(d.desc)}">` +
            `<b>${esc(d.name)}</b><i>${esc(d.kind)}</i></button>`,
        )
        .join("");
      return `<h3 class="god-group">${kind.toUpperCase()}</h3><div class="god-row">${rows}</div>`;
    }).join("");

    // Join-ready settlement prefabs: one click plops a sealed, recognized
    // building (bed + light + station) ahead of the player on their claim.
    const prefabRows = HOMESTEAD_PREFABS.map((prefab) =>
      `<button class="god-item god-prefab" data-prefab="${esc(prefab.id)}" title="join-ready: ${esc(prefab.functions.join(" + "))}">` +
      `<b>${esc(prefab.name)}</b><i>${esc(prefab.functions.join("+"))}</i></button>`,
    ).join("");
    const prefabGroup = `<h3 class="god-group">PLOP PREFAB · JOIN-READY BUILDINGS</h3><div class="god-row">${prefabRows}</div>`;

    const qtyBtns = QUANTITIES.map((q) => `<button data-q="${q}">${q === 999 ? "MAX" : q}</button>`).join("");
    const creditBtns = CREDIT_AMOUNTS.map((c) => `<button data-credit="${c}">+${creditLabel(c)}</button>`).join("");

    const enemyGrid = ENEMY_ROWS.map(
      (row) =>
        `<div class="god-enemy-rowlabel">${row.label}</div>` +
        row.kinds
          .map((k) => `<button class="god-enemy-cell" data-kind="${esc(k.kind)}">${esc(k.name)}</button>`)
          .join(""),
    ).join("");
    const countBtns = ENEMY_COUNTS.map((c) => `<button data-c="${c}">${c}</button>`).join("");
    const vehicleCards = Object.values(VEHICLES).map((v) =>
      `<button class="god-vehicle" data-vehicle="${esc(v.id)}"><b>${esc(v.name)}</b>` +
      `<i>${v.mass} KG · ${v.handling.maxSpeed} M/S · ${v.seats.length} SEATS</i></button>`,
    ).join("");
    const tunerFields = VEHICLE_TUNERS.map((f) =>
      `<label class="god-tuner"><span>${esc(f.label)}</span><input type="number" step="${f.step}" data-tune="${f.key}"></label>`,
    ).join("");

    // Dev-only: these pages are served by vite dev and are not build inputs.
    const dev = import.meta.env.DEV;
    const harnessTab = dev ? `<button class="god-tab" data-tab="harnesses">HARNESSES</button>` : "";
    // The Cutscene Lab is an in-WORLD overlay (not a separate page), so it dispatches an action
    // instead of opening a URL. Only shown when its handler is wired.
    const cutsceneEntry =
      dev && this.handlers.onCutscene
        ? `<button class="god-item god-harness" data-harness-action="cutscene" title="in-world cutscene authoring">` +
          `<b>CUTSCENE LAB</b><i>author anchor-relative camera cutscenes in-world</i></button>`
        : "";
    const harnessPane = dev
      ? `<div class="god-pane" data-pane="harnesses" style="display:none">
          <div class="god-row">${cutsceneEntry}${HARNESSES.map(
            (h) =>
              `<button class="god-item god-harness" data-path="${esc(h.path)}" title="${esc(h.path)}">` +
              `<b>${esc(h.name)}</b><i>${esc(h.desc)}</i></button>`,
          ).join("")}</div>
          <div class="god-hint">page harnesses open in a new tab &nbsp;·&nbsp; dev server only (not in a production build)</div>
        </div>`
      : "";

    this.el = document.createElement("div");
    this.el.id = "god-panel";
    this.el.innerHTML = `
      <div class="god-card">
        <h2>GOD MODE</h2>
        <div class="god-tabs">
          <button class="god-tab god-tab-on" data-tab="items">ITEMS</button>
          <button class="god-tab" data-tab="credits">CREDITS</button>
          <button class="god-tab" data-tab="enemies">ENEMIES</button>
          <button class="god-tab" data-tab="vehicles">VEHICLES</button>
          ${harnessTab}
          <button class="god-tab god-perf" data-perf>PERF: OFF</button>
        </div>
        <div class="god-pane" data-pane="items">
          <div class="god-qty"><label>QUANTITY</label>${qtyBtns}</div>
          ${prefabGroup}
          ${itemGroups}
          <div class="god-hint">click an item to spawn it into your inventory &nbsp;·&nbsp; esc: close</div>
        </div>
        <div class="god-pane" data-pane="credits" style="display:none">
          <h3 class="god-group">QUICK GRANT</h3>
          <div class="god-row god-credit-quick">${creditBtns}</div>
          <h3 class="god-group">CUSTOM AMOUNT</h3>
          <div class="god-credit-custom">
            <input type="number" step="100" min="0" placeholder="amount" data-credit-input>
            <button data-credit-add>ADD</button>
            <button data-credit-sub>SUBTRACT</button>
          </div>
          <div class="god-hint">credits are added to your balance server-side &nbsp;·&nbsp; the balance can't go below zero &nbsp;·&nbsp; esc: close</div>
        </div>
        <div class="god-pane" data-pane="enemies" style="display:none">
          <div class="god-enemy-head"><span></span><span>FAST · LIGHT</span><span>MEDIUM</span><span>SLOW · HEAVY</span></div>
          <div class="god-enemy-grid">${enemyGrid}</div>
          <div class="god-qty"><label>COUNT</label>${countBtns}</div>
          <div class="god-qty" data-stance-row><label>STANCE</label>
            <button data-stance="hostile" class="on">HOSTILE</button>
            <button data-stance="calm">CALM</button>
          </div>
          <button class="god-place" disabled>PLACE ON GROUND</button>
          <div class="god-hint">pick a type + count, then PLACE &nbsp;·&nbsp; aim & click the ground · scroll: spread · esc/right-click: cancel<br>
          CALM = short guard leash: they wander, pair up &amp; face each other — walk into their midst (~12&nbsp;m) to provoke. the idle-life observation mode</div>
        </div>
        <div class="god-pane" data-pane="vehicles" style="display:none">
          <div class="god-vehicle-list">${vehicleCards}</div>
          <h3 class="god-group">RUNTIME TUNING <small>blank = authored value</small></h3>
          <div class="god-tuning-grid">${tunerFields}</div>
          <div class="god-vehicle-actions"><button data-vehicle-reset>RESET OVERRIDES</button><button data-vehicle-spawn>SPAWN / REPLACE</button></div>
          <h3 class="god-group">ASSET VEHICLES <small>saved in the asset lab</small></h3>
          <div class="god-row" data-asset-vehicles><i>open the panel to load…</i></div>
          <div class="god-hint">tuning is session-only · the selected authored id is retained for network fallback</div>
        </div>
        ${harnessPane}
      </div>
    `;
    this.el.style.display = "none";
    document.body.appendChild(this.el);

    // Tab switching.
    const panes = this.el.querySelectorAll<HTMLElement>(".god-pane");
    // [data-tab] only — the PERF button shares the tab styling but is a toggle,
    // not a pane switch (without the filter its click would hide every pane).
    for (const tab of this.el.querySelectorAll<HTMLElement>(".god-tab[data-tab]")) {
      tab.addEventListener("click", () => {
        for (const t of this.el.querySelectorAll(".god-tab[data-tab]")) t.classList.toggle("god-tab-on", t === tab);
        for (const p of panes) p.style.display = p.dataset.pane === tab.dataset.tab ? "" : "none";
      });
    }

    // PERF: live overlay toggle — state paints from the handler so a reload
    // that restored the persisted flag still shows the true state on open().
    const perfBtn = this.el.querySelector<HTMLElement>("[data-perf]");
    this.paintPerf = () => {
      if (!perfBtn) return;
      const on = this.handlers.perfOn();
      perfBtn.textContent = on ? "PERF: ON" : "PERF: OFF";
      perfBtn.classList.toggle("god-tab-on", on);
    };
    perfBtn?.addEventListener("click", () => {
      this.handlers.onTogglePerf();
      this.paintPerf();
    });

    // ITEMS: quantity selector.
    const qtyBtnEls = this.el.querySelectorAll<HTMLElement>('.god-pane[data-pane="items"] .god-qty button');
    for (const b of qtyBtnEls) {
      b.addEventListener("click", () => {
        this.qty = Number(b.dataset.q) || 1;
        for (const x of qtyBtnEls) x.classList.toggle("on", x === b);
      });
    }
    qtyBtnEls[0]?.classList.add("on");

    // ITEMS: spawn on click.
    this.el.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>(".god-item");
      if (!btn) return;
      // PREFABS: close straight to gameplay (no ESC menu) so the plop shows.
      if (btn.dataset.prefab) {
        this.close(true);
        this.handlers.onPlopPrefab?.(btn.dataset.prefab);
        return;
      }
      if (!btn.dataset.id) return;
      this.handlers.onSpawn(btn.dataset.id, this.qty);
      btn.classList.add("spawned");
      window.setTimeout(() => btn.classList.remove("spawned"), 280);
    });

    // CREDITS: quick-grant buttons add a fixed denomination.
    const grantCredits = (amount: number, flash?: HTMLElement): void => {
      if (!Number.isFinite(amount) || amount === 0) return;
      this.handlers.onAddCredits(amount);
      if (flash) {
        flash.classList.add("spawned");
        window.setTimeout(() => flash.classList.remove("spawned"), 280);
      }
    };
    for (const b of this.el.querySelectorAll<HTMLElement>(".god-credit-quick button")) {
      b.addEventListener("click", () => grantCredits(Number(b.dataset.credit) || 0, b));
    }
    // CREDITS: custom amount — ADD grants it, SUBTRACT removes it (server clamps ≥ 0).
    const creditInput = this.el.querySelector<HTMLInputElement>("[data-credit-input]");
    const customCredits = (sign: 1 | -1): void => {
      const n = Math.abs(Number(creditInput?.value));
      if (!Number.isFinite(n) || n === 0) return;
      grantCredits(sign * n);
    };
    this.el.querySelector<HTMLElement>("[data-credit-add]")?.addEventListener("click", () => customCredits(1));
    this.el.querySelector<HTMLElement>("[data-credit-sub]")?.addEventListener("click", () => customCredits(-1));
    creditInput?.addEventListener("keydown", (e) => {
      if (e.key === "Enter") customCredits(1);
    });

    // HARNESSES: page harnesses open in a new tab (the game keeps running behind it and keeps its
    // socket — "noopener" stops the harness page reaching back via window.opener). The Cutscene Lab
    // is instead an in-world overlay, so it closes this panel and hands off to its opener.
    this.el.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>(".god-harness");
      if (!btn) return;
      if (btn.dataset.harnessAction === "cutscene") {
        this.close(true); // suppress onClose so the ESC menu doesn't flash back before the editor opens
        this.handlers.onCutscene?.();
        return;
      }
      if (btn.dataset.path) window.open(btn.dataset.path, "_blank", "noopener");
    });

    // ENEMIES: cell selection.
    this.placeBtn = this.el.querySelector<HTMLButtonElement>(".god-place")!;
    const placeBtn = this.placeBtn;
    const cellEls = this.el.querySelectorAll<HTMLElement>(".god-enemy-cell");
    for (const cell of cellEls) {
      cell.addEventListener("click", () => {
        this.enemyKind = cell.dataset.kind ?? null;
        for (const c of cellEls) c.classList.toggle("on", c === cell);
        placeBtn.disabled = this.enemyKind === null;
      });
    }
    // ENEMIES: count selector (⚠ scoped to [data-c] — the stance row below is
    // also a .god-qty and must not be swept into the count toggle group).
    const countEls = this.el.querySelectorAll<HTMLElement>('.god-pane[data-pane="enemies"] .god-qty button[data-c]');
    for (const b of countEls) {
      b.addEventListener("click", () => {
        this.enemyCount = Number(b.dataset.c) || 1;
        for (const x of countEls) x.classList.toggle("on", x === b);
      });
    }
    countEls[0]?.classList.add("on");
    // ENEMIES: stance selector — HOSTILE spawns fight on sight (as ever); CALM
    // spawns carry a tight guard leash so their idle life is observable.
    const stanceEls = this.el.querySelectorAll<HTMLElement>("[data-stance]");
    for (const b of stanceEls) {
      b.addEventListener("click", () => {
        this.enemyCalm = b.dataset.stance === "calm";
        for (const x of stanceEls) x.classList.toggle("on", x === b);
      });
    }

    // VEHICLES: choose an authored base, optionally override bounded tuning values, then spawn.
    const vehicleEls = this.el.querySelectorAll<HTMLElement>(".god-vehicle");
    const tuneInputs = this.el.querySelectorAll<HTMLInputElement>("[data-tune]");
    const refreshVehicle = (): void => {
      const def = VEHICLES[this.vehicleId];
      if (!def) return;
      for (const v of vehicleEls) v.classList.toggle("on", v.dataset.vehicle === this.vehicleId);
      for (const input of tuneInputs) {
        const field = VEHICLE_TUNERS.find((f) => f.key === input.dataset.tune);
        if (field) input.placeholder = String(field.value(def));
      }
    };
    for (const v of vehicleEls) v.addEventListener("click", () => {
      this.vehicleId = v.dataset.vehicle ?? this.vehicleId;
      for (const input of tuneInputs) input.value = "";
      refreshVehicle();
    });
    this.el.querySelector<HTMLElement>("[data-vehicle-reset]")?.addEventListener("click", () => {
      for (const input of tuneInputs) input.value = "";
      refreshVehicle();
    });
    this.el.querySelector<HTMLElement>("[data-vehicle-spawn]")?.addEventListener("click", () => {
      const base = VEHICLES[this.vehicleId];
      if (!base) return;
      const tuning: VehicleTuning = {};
      for (const input of tuneInputs) {
        if (input.value.trim() === "") continue;
        const n = Number(input.value);
        const key = input.dataset.tune as keyof VehicleTuning | undefined;
        if (key && Number.isFinite(n)) tuning[key] = n;
      }
      this.close(true);
      this.handlers.onSpawnVehicle(tuneVehicle(base, tuning));
    });
    refreshVehicle();

    // ENEMIES: PLACE → hand off to the reticle.
    placeBtn.addEventListener("click", () => {
      if (!this.enemyKind) return;
      const kind = this.enemyKind;
      const count = this.enemyCount;
      const calm = this.enemyCalm;
      this.close(true); // suppress onClose so the ESC menu is NOT reopened before onSpawnEnemy hides it
      this.handlers.onSpawnEnemy(kind, count, calm);
    });
  }

  /** Refresh the lab-saved vehicle list (async — Firestore-backed). */
  private populateAssetVehicles(): void {
    const host = this.el.querySelector<HTMLElement>("[data-asset-vehicles]");
    if (!host) return;
    this.handlers.listAssetVehicles().then((entries) => {
      host.innerHTML = entries.length
        ? entries.map((entry) =>
            `<button class="god-vehicle" data-asset-vehicle="${esc(entry.id)}"><b>${esc(entry.name)}</b><i>lab asset</i></button>`,
          ).join("")
        : "<i>none saved — build one in the ASSET LAB</i>";
      for (const button of host.querySelectorAll<HTMLElement>("[data-asset-vehicle]")) {
        button.addEventListener("click", () => {
          this.close(true);
          this.handlers.onSpawnAssetVehicle(button.dataset.assetVehicle!);
        });
      }
    }).catch(() => { host.textContent = "sign in to list saved assets"; });
  }

  open(): void {
    // Reset ENEMIES selection state so stale kind/highlight can't persist across opens.
    this.enemyKind = null;
    for (const cell of this.el.querySelectorAll<HTMLElement>(".god-enemy-cell")) {
      cell.classList.remove("on");
    }
    this.placeBtn.disabled = true;
    this.populateAssetVehicles();
    this.paintPerf();

    this.visible = true;
    this.el.style.display = "flex";
  }

  close(suppressOnClose = false): void {
    if (!this.visible) return;
    this.visible = false;
    this.el.style.display = "none";
    if (!suppressOnClose) this.handlers.onClose();
  }

  toggle(): void {
    if (this.visible) this.close();
    else this.open();
  }
}
